<template>
  <div>
    <page-header
      :page="pageConfig"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
    >
      <template v-slot:customActions>
        <action-button
          v-for="action in headerActions"
          :key="action.text"
          :i="action.icon"
          :text="action.text"
          :variant="action.variant"
          :disabled="action.disabled"
          class="ml-2"
          @click="action.handler()"
        />
      </template>
    </page-header>

    <board :details="referralInfo" class="mb-3" />

    <custom-error
      v-if="isSignatureRejected || isDocumentCancelled"
      is-alert-mode
      variant="warning"
      class="mb-3"
      :message="cancellationReason"
    />

    <custom-tab
      ref="minuteTabs"
      class="minute-tabs"
      :default-active-tab="activeTab"
      @input="handleTabChange"
    >
      <custom-tab-item title="Información General" :lazy="false">
        <general-information-tab
          class="minute-tabs__content"
          ref="generalInformation"
          :initial-data="minute"
          :minute-id="minuteId"
          :is-active="activeTab === 0"
          :readonly="isReadOnly"
          @change="updateGeneralInformation"
          @ready="handleGeneralInfoReady"
        />
      </custom-tab-item>
      <custom-tab-item
        title="Editor de Minuta"
        :lazy="false"
        :nav-link-class="{
          'minute-tabs__disabled-tab': !isMinuteEditorEnabled,
          'minute-tabs__error-tab': hasPendingPlaceholders,
        }"
        @click="handleDisabledTabClick(1)"
      >
        <custom-error
          v-if="hasPendingPlaceholders"
          class="mb-3"
          :message="pendingPlaceholdersMessage"
        />
        <minute-editor-tab
          ref="editorTab"
          class="minute-tabs__content"
          v-model="editorContent"
          :general-information="generalInformation"
          :readonly="isReadOnly"
          @preview="previewMinute"
          @auto-save="handleAutoSave"
        />
      </custom-tab-item>
      <custom-tab-item
        title="Firma"
        :lazy="false"
        :nav-link-class="{ 'minute-tabs__disabled-tab': !isSignatureEnabled }"
        @click="handleDisabledTabClick(2)"
      >
        <minute-signature-tab
          ref="signatureTab"
          class="minute-tabs__content"
          v-model="signatureInformation"
          :minute="minute || {}"
          :readonly="isReadOnly"
          :rebuild-template="rebuildSignatureTemplate"
          :student-sie-id="signatureStudentSieId"
          @configure-sign="handleConfigureSign"
          @download-template="downloadManuscriptTemplate"
          @download-signed-document="downloadSignedDocument"
          @hub-info-updated="signatureHubInfo = $event"
          @ready="isSignatureTabReady = true"
        />
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import PageHeader from 'PageHeader';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import alertMixin from '@/mixin/alert.mixin';
import loadingMixin from '@/mixin/loading.mixin';
import compuMinuteService from '@/modules/compu-minutes-editor/api';
import documentsService from '@/services/documents';
import documentService from '@/services/pei/general';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import { getSignatureHubInfo } from '@/services/signatureHub/api';
import Board from '../components/CompuMinuteBoard.vue';
import GeneralInformationTab from '../components/GeneralInformationTab.vue';
import MinuteEditorTab from '../components/MinuteEditorTab.vue';
import MinuteSignatureTab from '../components/MinuteSignatureTab.vue';
import compuMinuteStatus from '@/utils/constants/compuMinuteStatus.js';
import digitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus.js';

const _InSignatureStatusId = 2;
const _CompletedStatusId = 3;
const _CancelledStatusId = 4;
const _SignatureRejectedStatusId = 5;
const _DocumentCancelledStatusId = 6;
import { getCompuMinuteStatusColor } from '../helpers/compu-minute-status.helper';

const _DraftStatusId = 1;
const _GeneralInformationStepId = 1;
const _MinuteEditorStepId = 2;
const _SignatureStepId = 3;
const _DraftStatusName = 'borrador';
const _ReturnToDraftPermission = 'pei.compu-minutes-editor.returntodraft';

export default {
  name: 'CreateCompuMinute',
  mixins: [alertMixin, loadingMixin],
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },
  components: {
    ActionButton,
    Board,
    CustomError,
    CustomTab,
    CustomTabItem,
    GeneralInformationTab,
    MinuteEditorTab,
    MinuteSignatureTab,
    PageHeader,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // Al volver de SignatureHub se conserva el origen previo para poder regresar a él.
      if (from?.name === 'SignatureHub') {
        vm.originPath = sessionStorage.getItem('compuMinuteOriginPath');
        return;
      }

      vm.originPath = from?.path || null;
      sessionStorage.setItem('compuMinuteOriginPath', vm.originPath || '');
    });
  },
  beforeDestroy() {
    this.HideWait();
  },
  data() {
    return {
      originPath: null,
      generalInformation: {},
      minute: null,
      activeTab: 0,
      isMinuteEditorEnabled: false,
      isSignatureEnabled: false,
      editorContent: '',
      lastSavedEditorContent: '',
      lastSavedGeneralInformation: null,
      skipNextRouteFetch: false,
      isAdvancingToEditor: false,
      isSavingEditor: false,
      isRestoringMinute: false,
      isLeavingMinute: false,
      persistedStepId: _GeneralInformationStepId,
      signatureHubInfo: null,
      signatureInformation: {
        isElectronicSignature: null,
        signedDocumentFile: null,
        participants: [],
        hasCompleteParticipants: false,
      },
      rebuildSignatureTemplate: false,
      isGeneralInfoTabReady: false,
      isSignatureTabReady: false,
      hasInitialLoadFailed: false,
      isChangingTab: false,
      pendingTabIndex: null,
      pendingPlaceholderLabels: [],
    };
  },
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-file-signature',
        title: 'Editor de Minutas de COMPU',
        routes: [
          'Inicio',
          'PEI',
          'Editor de Minutas de COMPU',
          this.minuteId ? 'Editar Minuta' : 'Crear Minuta',
        ],
      };
    },
    minuteId() {
      const id = Number(this.id);
      return Number.isInteger(id) && id > 0 ? id : null;
    },
    referralInfo() {
      return [
        [
          {
            label: 'Estatus:',
            data: this.minuteStatusName,
            icon: 'fas fa-file',
            statusDotColor: this.minuteStatusColor,
          },
          {
            label: 'Año Académico:',
            data: this.generalInformation.academicYear?.name || '-',
            icon: 'fas fa-calendar-alt',
          },
          {
            label: 'Creado por:',
            data:
              this.minute?.createdBy ||
              this.generalInformation.createdBy ||
              '-',
            icon: 'fas fa-user',
          },
          {
            label: 'Número de cita:',
            data:
              this.generalInformation.compuAppointmentId ||
              this.minute?.compuAppointmentId ||
              this.getObjectId(
                this.generalInformation.coordinatedAppointment
              ) ||
              this.getObjectId(
                this.minute?.coordinatedAppointment ?? this.minute?.appointment
              ) ||
              '-',
            icon: 'fas fa-file',
          },
        ],
      ];
    },
    persistedMinuteId() {
      return this.minute?.id ?? this.minute?.compuMinuteId ?? this.minuteId;
    },
    signatureStudentSieId() {
      const student =
        this.generalInformation?.students?.[0] ??
        this.minute?.selectedStudent ??
        this.minute?.student ??
        null;

      return (
        this.getStudentSieId(student) ??
        this.minute?.studentSieId ??
        this.generalInformation?.studentSieId ??
        null
      );
    },
    isInitialLoadReady() {
      return (
        this.hasInitialLoadFailed ||
        (this.isGeneralInfoTabReady && this.isSignatureTabReady)
      );
    },
    isUiBlocked() {
      // Un único gate para el overlay de bloqueo: cubre tanto la carga
      // inicial de la pantalla como el guardado al cambiar de tab.
      return !this.isInitialLoadReady || this.isChangingTab;
    },
    hasPendingPlaceholders() {
      return this.pendingPlaceholderLabels.length > 0;
    },
    pendingPlaceholdersMessage() {
      if (!this.hasPendingPlaceholders) {
        return '';
      }

      const items = this.pendingPlaceholderLabels
        .map((label) => `- ${label}`)
        .join('<br>');

      return `No se puede completar la minuta.<br>Debe completar los siguientes campos:<br><br>${items}.`;
    },
    isDraft() {
      const statusId = this.minuteStatusId;
      const statusName = this.minuteStatusName;

      if (statusId) {
        return Number(statusId) === _DraftStatusId;
      }

      return (
        !statusName || statusName.trim().toLowerCase() === _DraftStatusName
      );
    },
    isCompleted() {
      return Number(this.minuteStatusId) === compuMinuteStatus.Completada;
    },
    isEnFirmaElectronica() {
      return (
        Number(this.minuteStatusId) === compuMinuteStatus.EnProcesoFirma &&
        this.minute?.isElectronicSignature === true
      );
    },
    isSignatureRejected() {
      return Number(this.minuteStatusId) === compuMinuteStatus.FirmaRechazada;
    },
    isDocumentCancelled() {
      return (
        Number(this.minuteStatusId) === compuMinuteStatus.DocumentoCancelado
      );
    },
    isCancelled() {
      return Number(this.minuteStatusId) === compuMinuteStatus.Cancelada;
    },
    canReturnToDraft() {
      const hasPermission = this.userHasPermissions(_ReturnToDraftPermission);
      const canReturn =
        !this.isDraft &&
        (this.isSignatureRejected ||
          this.isDocumentCancelled ||
          this.isCancelled ||
          hasPermission);

      if (this.isDraft) {
        return false;
      }

      return canReturn;
    },
    cancellationReason() {
      return this.minute?.digitalSignatureDocumentCancellationReason || null;
    },
    signedDocumentId() {
      return (
        this.minute?.signedDocumentId ||
        this.minute?.manuscriptSignedDocumentId ||
        null
      );
    },
    isSignatureInProcess() {
      return Boolean(this.signatureHubInfo?.documentId);
    },
    isReadOnly() {
      return !this.isDraft || this.isSignatureInProcess;
    },
    minuteStatusId() {
      return this.minute?.statusId ?? this.generalInformation.statusId;
    },
    minuteStatusName() {
      return (
        this.minute?.statusName ||
        this.generalInformation.statusName ||
        'Borrador'
      );
    },
    minuteStatusColor() {
      return getCompuMinuteStatusColor(
        this.minuteStatusId,
        this.minuteStatusName
      );
    },
    headerActions() {
      const returnAction = {
        icon: 'fas fa-arrow-circle-left',
        text: 'Regresar',
        variant: 'outline-success',
        handler: this.goBack,
      };

      const printAction = this.persistedMinuteId
        ? {
            icon: 'fas fa-print',
            text: 'Imprimir',
            variant: 'success',
            handler: this.handlePrintMinute,
          }
        : null;

      if (!this.isDraft || this.isSignatureInProcess) {
        const actions = [returnAction, printAction];

        if (this.canReturnToDraft) {
          actions.push({
            icon: 'fas fa-undo-alt',
            text: 'Devolver a Borrador',
            variant: 'outline-success',
            handler: this.revertToDraft,
          });
        }

        if (this.isEnFirmaElectronica) {
          actions.push({
            icon: 'fas fa-check-circle',
            text: 'Completar',
            variant: 'success',
            handler: this.completeElectronicMinute,
          });
        }

        return actions.filter(Boolean);
      }

      if (this.activeTab === 2) {
        const actions = [returnAction, printAction];

        if (this.signatureInformation?.isElectronicSignature === false) {
          actions.push({
            icon: 'fas fa-check-circle',
            text: 'Completar',
            variant: 'success',
            disabled: !this.canCompleteManuscriptSignature,
            handler: this.completeMinute,
          });
        }

        return actions.filter(Boolean);
      }

      if (this.activeTab === 1) {
        return [
          returnAction,
          printAction,
          {
            icon: 'fas fa-arrow-alt-circle-right',
            text: 'Continuar',
            variant: 'success',
            handler: this.continueToSignature,
          },
        ].filter(Boolean);
      }

      return [
        returnAction,
        printAction,
        {
          icon: 'fas fa-arrow-alt-circle-right',
          text: 'Continuar',
          variant: 'success',
          handler: this.continueMinute,
        },
      ].filter(Boolean);
    },
    canCompleteManuscriptSignature() {
      return (
        this.signatureInformation?.isElectronicSignature === false &&
        this.signatureInformation?.hasCompleteParticipants === true &&
        Boolean(this.signatureInformation?.signedDocumentFile)
      );
    },
    isValidEditorContent() {
      const contentSanitized = this.editorContent
        .replace(/<[^>]*>/g, '')
        .replace(/\s/g, '');

      return Boolean(contentSanitized);
    },
  },
  watch: {
    id: {
      immediate: true,
      async handler() {
        if (this.skipNextRouteFetch) {
          this.skipNextRouteFetch = false;
          return;
        }

        this.minute = null;
        this.signatureHubInfo = null;
        this.isGeneralInfoTabReady = false;
        this.hasInitialLoadFailed = false;

        if (this.minuteId) {
          try {
            await this.fetchMinute();
          } catch (error) {
            this.hasInitialLoadFailed = true;
          }
        }
      },
    },
    isUiBlocked: {
      immediate: true,
      handler(value) {
        if (value) {
          this.ShowWait();
        } else {
          this.HideWait();
        }
      },
    },
  },
  methods: {
    async completeElectronicMinute() {
      if (!this.validateNoPendingPlaceholders()) {
        return;
      }

      const {
        Signed,
        Cancelled,
        RenderedIneffective,
        Rejected,
      } = digitalSignatureDocumentStatus;

      try {
        const { data: hubInfo } = await getSignatureHubInfo(
          SignatureHubTypes.COMPU_MINUTE,
          this.persistedMinuteId
        );

        const docStatusId = hubInfo?.documentInfo?.documentStatusId;

        if (
          docStatusId === Cancelled ||
          docStatusId === RenderedIneffective ||
          docStatusId === Rejected
        ) {
          await compuMinuteService.syncElectronicCancellation(
            this.persistedMinuteId
          );
          await this.fetchMinute();
          await this.$refs.signatureTab?.fetchSignatureHubInfo();
          return;
        }

        if (docStatusId !== Signed) {
          this.ShowToast(
            'Minuta en Proceso de Firma',
            'La minuta aún se encuentra en proceso de firma. Verifique que todos los participantes hayan completado su firma antes de continuar.',
            'error'
          );
          return;
        }

        await compuMinuteService.completeElectronic(this.persistedMinuteId);

        this.ShowToast(
          'Proceso Exitoso',
          'La minuta ha sido completada exitosamente.',
          'success'
        );

        await this.fetchMinute();
        await this.$refs.signatureTab?.fetchSignatureHubInfo();
      } catch (err) {
        this.ShowToast(
          'Error',
          err?.response?.data?.message ||
            'No fue posible verificar el estado del proceso de firma. Intente nuevamente más tarde o contacte al administrador.',
          'error'
        );
      }
    },
    async handlePrintMinute() {
      if (this.isCompleted && this.signedDocumentId) {
        await this.downloadSignedDocument(this.signedDocumentId);
      } else {
        await this.downloadManuscriptTemplate();
      }
    },
    async revertToDraft() {
      if (!this.persistedMinuteId) return;

      const response = await compuMinuteService
        .revertToDraft(this.persistedMinuteId)
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });

      if (response?.data) {
        this.ShowToast(
          'Proceso Exitoso',
          'La minuta ha sido devuelta a Borrador exitosamente.',
          'success'
        );
        await this.fetchMinute();
        await this.$refs.signatureTab?.fetchSignatureHubInfo();
      }
    },
    async goBack() {
      this.isLeavingMinute = true;

      try {
        if (this.isDraft) {
          await this.saveDraftBeforeLeaving();
        }

        await this.navigateToMinuteList();
      } finally {
        this.isLeavingMinute = false;
      }
    },
    async saveDraftBeforeLeaving() {
      if (!this.persistedMinuteId) {
        return;
      }

      try {
        if (this.activeTab === 0) {
          if (!this.isGeneralInformationReady(this.generalInformation)) {
            return;
          }

          await this.saveGeneralInformation();
        }

        if (this.activeTab === 2) {
          if (this.isGeneralInformationReady(this.generalInformation)) {
            await this.saveSignature({ showSuccess: false });
          }
        }
      } catch (error) {
        this.ShowToast(
          'Error',
          'No se pudo guardar la minuta antes de regresar.',
          'error'
        );
      }
    },
    resolveBackRoute() {
      if (this.originPath?.startsWith('/pei/detail')) {
        return { path: '/pei/detail', query: { tab: 'Tab10' } };
      }

      if (
        this.originPath?.startsWith('/student/AddProcessDeterminationTriannual')
      ) {
        return { path: this.originPath, query: { isAdd: 'true' } };
      }

      if (this.originPath?.startsWith('/student/evaluationdiscuss/')) {
        return { path: this.originPath };
      }

      if (this.originPath?.startsWith('/student/AddEditEvaluations')) {
        return { path: this.originPath, query: { fromCompuMinute: 'true' } };
      }

      return { name: 'compu-minutes-editor' };
    },
    async navigateToMinuteList() {
      const listRoute = this.resolveBackRoute();
      const resolvedRoute = this.$router.resolve(listRoute).route;
      const listPath = resolvedRoute.fullPath;

      try {
        if (this.$route.name !== resolvedRoute.name) {
          await this.$router.push(listRoute);
          await this.$nextTick();
        }

        if (this.$route.name !== resolvedRoute.name) {
          window.location.href = listPath;
        }
      } catch (error) {
        if (error?.name !== 'NavigationDuplicated') {
          window.location.href = listPath;
        }
      }
    },
    async continueMinute() {
      if (!this.isDraft) {
        return;
      }

      const isValid = await this.$refs.generalInformation.validate();

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información requerida para continuar.',
          'error'
        );
        return;
      }

      this.isAdvancingToEditor = true;

      try {
        await this.saveGeneralInformation({
          stepId: _MinuteEditorStepId,
        });
        this.isMinuteEditorEnabled = true;
        await this.$nextTick();
        this.setActiveTab(1);
      } finally {
        this.isAdvancingToEditor = false;
      }
    },
    updateGeneralInformation(value) {
      this.generalInformation = value;

      if (this.isLeavingMinute) {
        return;
      }

      if (this.isRestoringMinute) {
        const stepId = this.getMinuteStepId();

        this.isMinuteEditorEnabled =
          !this.isDraft || stepId >= _MinuteEditorStepId;
        this.isSignatureEnabled = !this.isDraft || stepId >= _SignatureStepId;
        this.isRestoringMinute = false;

        return;
      }

      if (
        this.isDraft &&
        this.persistedMinuteId &&
        this.isGeneralInformationReady(value)
      ) {
        this.isMinuteEditorEnabled = true;
      }

      if (
        this.isDraft &&
        this.isMinuteEditorEnabled &&
        !this.isAdvancingToEditor &&
        !this.isSavingEditor &&
        !this.isGeneralInformationReady(value)
      ) {
        this.isMinuteEditorEnabled =
          this.persistedStepId >= _MinuteEditorStepId;
        this.isSignatureEnabled = this.persistedStepId >= _SignatureStepId;

        if (this.activeTab > 0 && !this.canOpenTab(this.activeTab)) {
          this.setActiveTab(0);
        }
      }
    },
    handleGeneralInfoReady() {
      this.isGeneralInfoTabReady = true;

      if (this.persistedMinuteId) {
        this.lastSavedGeneralInformation = this.buildComparableGeneralInfoSnapshot();
      }
    },
    async handleTabChange(tabIndex) {
      if (tabIndex === null || tabIndex === undefined) return;

      if (this.isChangingTab) {
        // Ya hay un cambio de tab en curso (validando/guardando). Guardamos
        // la intención más reciente del usuario para procesarla apenas
        // termine el cambio en curso, y revertimos el salto visual que ya
        // hizo b-tabs mientras tanto.
        this.pendingTabIndex = tabIndex;
        this.setActiveTab(this.activeTab);
        return;
      }

      this.isChangingTab = true;
      let nextTabIndex = tabIndex;

      try {
        while (nextTabIndex !== null) {
          const targetIndex = nextTabIndex;
          this.pendingTabIndex = null;

          // Guardar antes de salir del tab actual
          if (this.isDraft && this.activeTab !== targetIndex) {
            // Saliendo del tab 0 (Información General)
            if (this.activeTab === 0) {
              const isValid = await this.$refs.generalInformation?.validate();

              if (!isValid) {
                this.setActiveTab(this.activeTab);
                this.pendingTabIndex = null;
                this.ShowToast(
                  'Alerta',
                  'Favor de completar la información requerida para continuar.',
                  'error'
                );
                break;
              }

              if (
                this.persistedMinuteId &&
                this.isGeneralInformationReady(this.generalInformation)
              ) {
                try {
                  await this.saveGeneralInformation();
                } catch (error) {
                  this.ShowToast(
                    'Error',
                    'No se pudo guardar la información general.',
                    'error'
                  );
                }
              }
            }

            // Saliendo del tab 2 (Firma)
            if (this.activeTab === 2) {
              if (this.persistedMinuteId) {
                try {
                  await this.saveSignature({ showSuccess: false });
                } catch (error) {
                  this.ShowToast(
                    'Error',
                    'No se pudo guardar la información de firma.',
                    'error'
                  );
                }
              }
            }
          }

          // Si mientras validábamos/guardábamos llegó un click más reciente
          // (this.pendingTabIndex ya no es null), no finalizamos este cambio
          // con un destino obsoleto: lo descartamos y en la próxima vuelta
          // del while procesamos directamente el destino más reciente.
          if (this.pendingTabIndex === null) {
            if (!this.canOpenTab(targetIndex)) {
              this.setActiveTab(this.activeTab);
            } else {
              this.activeTab = targetIndex;
            }
          }

          nextTabIndex = this.pendingTabIndex;
        }
      } finally {
        this.isChangingTab = false;
        this.pendingTabIndex = null;
      }
    },
    handleDisabledTabClick(tabIndex) {
      if (!this.canOpenTab(tabIndex)) {
        this.setActiveTab(this.activeTab);
      }
    },
    canOpenTab(tabIndex) {
      if (!this.isDraft) return true;
      if (tabIndex === 1) return this.isMinuteEditorEnabled;
      if (tabIndex === 2) return this.isSignatureEnabled;

      return true;
    },
    isGeneralInformationReady(information) {
      return Boolean(
        information?.students?.length &&
          information?.school &&
          information?.town &&
          information?.appointmentDate &&
          information?.purposes?.length &&
          this.hasValidPurposeDescriptions(information.purposes) &&
          (information?.hasCoordinatedAppointment !== true ||
            information?.coordinatedAppointment)
      );
    },
    hasValidPurposeDescriptions(purposes) {
      return purposes.every(
        (purpose) =>
          !this.isOtherPurpose(purpose) || purpose.otherDescription?.trim()
      );
    },
    isOtherPurpose(purpose) {
      return purpose?.name?.trim().toLowerCase() === 'otro';
    },
    setActiveTab(tabIndex) {
      this.activeTab = tabIndex;

      this.$nextTick(() => {
        if (this.$refs.minuteTabs) {
          this.$refs.minuteTabs.tabModel = tabIndex;
        }
      });
    },
    async fetchMinute() {
      try {
        const { data } = await compuMinuteService.getById(this.minuteId);
        this.isRestoringMinute = true;
        this.minute = data;
        this.editorContent = data?.content ?? '';
        this.lastSavedEditorContent = data?.content ?? '';
        this.signatureInformation = {
          isElectronicSignature: data?.isElectronicSignature ?? null,
          signedDocumentFile: null,
          participants: data?.participants ?? [],
          hasCompleteParticipants: false,
        };
        const stepId = this.getMinuteStepId(data);
        this.persistedStepId = stepId;

        this.isMinuteEditorEnabled =
          !this.isDraft || stepId >= _MinuteEditorStepId;
        this.isSignatureEnabled = !this.isDraft || stepId >= _SignatureStepId;

        const returnTab = sessionStorage.getItem('compuMinuteReturnTab');
        if (returnTab !== null) {
          sessionStorage.removeItem('compuMinuteReturnTab');
          await this.$nextTick();
          this.setActiveTab(Number(returnTab));
        }
      } catch (error) {
        this.ShowToast(
          'Error',
          'No se pudo cargar la información de la minuta.',
          'error'
        );
        throw error;
      }
    },
    async saveGeneralInformation({ stepId = null, syncMinute = true } = {}) {
      if (!this.isDraft) {
        return;
      }

      if (
        stepId == null &&
        this.persistedMinuteId &&
        this.lastSavedGeneralInformation !== null &&
        this.buildComparableGeneralInfoSnapshot() === this.lastSavedGeneralInformation
      ) {
        return;
      }

      // Indicar que debe descargarse nuevamente el template de firma
      this.rebuildSignatureTemplate = true;
      this.$nextTick(() => {
        this.rebuildSignatureTemplate = false;
      });

      const payload = this.buildGeneralInformationPayload({ stepId });
      const isCreatingMinute = !this.persistedMinuteId;
      const { data } = this.persistedMinuteId
        ? await compuMinuteService.update(this.persistedMinuteId, payload)
        : await compuMinuteService.create(payload);
      this.lastSavedGeneralInformation = this.buildComparableGeneralInfoSnapshot();
      const minuteData = data ? { ...data } : data;
      const hasCoordinatedAppointment = this.generalInformation
        .hasCoordinatedAppointment;

      if (minuteData && payload.compuAppointmentId == null) {
        minuteData.compuAppointmentId = null;
      }

      if (minuteData && typeof hasCoordinatedAppointment === 'boolean') {
        minuteData.hasCoordinatedAppointment = hasCoordinatedAppointment;
      }

      const createdMinuteId = minuteData?.id ?? minuteData?.compuMinuteId;

      if (syncMinute) {
        this.minute = this.mergeMinuteData(minuteData);
        this.editorContent = minuteData?.content ?? this.editorContent;
        this.lastSavedEditorContent = minuteData?.content ?? this.editorContent;
      }

      if (minuteData) {
        const savedStepId = this.getMinuteStepId(minuteData);
        this.persistedStepId = savedStepId;
        this.isMinuteEditorEnabled =
          !this.isDraft || savedStepId >= _MinuteEditorStepId;
        this.isSignatureEnabled =
          !this.isDraft || savedStepId >= _SignatureStepId;
      }

      if (isCreatingMinute && createdMinuteId) {
        this.skipNextRouteFetch = true;

        await this.$router.replace({
          name: 'compu-minutes-editor-detail',
          params: { id: createdMinuteId },
        });

        await this.fetchMinute();
      }
    },
    async saveEditor({ showSuccess = true, stepId = null } = {}) {
      if (!this.isDraft) {
        return;
      }

      if (!this.validateEditorContent()) {
        return;
      }

      await this.ensureMinuteExists();
      const shouldKeepEditorTab = showSuccess && this.activeTab === 1;

      this.isSavingEditor = true;

      try {
        const { data } = await compuMinuteService.saveEditor(
          this.persistedMinuteId,
          {
            content: this.editorContent,
            compuMinuteStepId: stepId,
          }
        );

        if (data) {
          const wasSignatureEnabled = this.isSignatureEnabled;
          // El backend persiste el contenido sin header-informativo; se conserva el del editor.
          this.minute = this.mergeMinuteData({ ...data, content: this.editorContent });
          this.lastSavedEditorContent = this.editorContent;

          const savedStepId = this.getMinuteStepId(data);
          this.persistedStepId = savedStepId;
          this.isMinuteEditorEnabled =
            !this.isDraft || savedStepId >= _MinuteEditorStepId;
          this.isSignatureEnabled =
            !this.isDraft || stepId === _SignatureStepId || wasSignatureEnabled;
        }

        await this.$nextTick();

        if (shouldKeepEditorTab) {
          this.setActiveTab(1);
        }

        if (showSuccess) {
          this.ShowToast(
            '',
            'La minuta ha sido guardada exitosamente.',
            'success'
          );
        }
      } finally {
        this.isSavingEditor = false;
      }
    },
    async handleAutoSave() {
      if (!this.isDraft || !this.persistedMinuteId) {
        return;
      }

      try {
        await compuMinuteService.autoSaveEditor(this.persistedMinuteId, {
          content: this.editorContent,
          compuMinuteStepId: null,
        });

        // Mostrar indicador de "Guardado" en el componente editor
        if (this.$refs.editorTab?.showSavedIndicator) {
          this.$refs.editorTab.showSavedIndicator();
        }
      } catch (error) {
        console.error('Error en auto-guardado');
      }
    },
    async continueToSignature() {
      if (!this.isDraft) {
        return;
      }

      if (!this.validateEditorContent()) {
        return;
      }

      await this.saveEditor({
        showSuccess: false,
        stepId: _SignatureStepId,
      });
      this.isSignatureEnabled = true;
      this.setActiveTab(2);
    },
    async saveSignature({ showSuccess = true } = {}) {
      if (!this.isDraft) {
        return;
      }

      await this.ensureMinuteExists();

      const { data } = await compuMinuteService.saveSignature(
        this.persistedMinuteId,
        {
          isElectronicSignature: this.signatureInformation
            .isElectronicSignature,
          participants: this.signatureInformation.participants ?? [],
        }
      );

      if (data) {
        this.updateMinuteData(data);
        this.signatureInformation = {
          ...this.signatureInformation,
          isElectronicSignature:
            data?.isElectronicSignature ??
            this.signatureInformation.isElectronicSignature,
          participants:
            data?.participants ?? this.signatureInformation.participants,
        };
      }

      if (showSuccess) {
        this.ShowToast(
          '',
          'La minuta ha sido guardada exitosamente.',
          'success'
        );
      }
    },
    async handleConfigureSign() {
      await this.saveSignature({ showSuccess: false });

      if (!this.persistedMinuteId) return;

      sessionStorage.setItem('compuMinuteReturnTab', '2');

      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.COMPU_MINUTE,
          relatedId: this.persistedMinuteId,
          from: 'compuMinute',
        },
      });
    },
    async completeMinute() {
      if (!this.canCompleteManuscriptSignature) {
        return;
      }

      if (!this.validateNoPendingPlaceholders()) {
        return;
      }

      await this.ensureMinuteExists();
      await this.saveSignature({ showSuccess: false });

      const payload = new FormData();
      payload.append(
        'isElectronicSignature',
        this.signatureInformation.isElectronicSignature
      );
      payload.append(
        'signedMinuteFile',
        this.signatureInformation.signedDocumentFile
      );

      const { data } = await compuMinuteService.completeManuscript(
        this.persistedMinuteId,
        payload
      );

      if (data) {
        this.updateMinuteData(data);
        this.signatureInformation = {
          isElectronicSignature: data?.isElectronicSignature ?? false,
          signedDocumentFile: null,
          participants: data?.participants ?? [],
          hasCompleteParticipants: true,
        };
      }

      this.ShowToast(
        'Proceso Exitoso',
        'La minuta ha sido completada exitosamente.',
        'success'
      );
    },
    async downloadManuscriptTemplate(onDownloaded) {
      await this.ensureMinuteExists();
      await this.saveSignature({ showSuccess: false });

      const response = await compuMinuteService
        .downloadManuscriptTemplate(this.persistedMinuteId)
        .catch((err) => {
          this.ShowToast(
            'Alerta',
            err?.response?.data?.message ||
              'No fue posible descargar el documento.',
            'error'
          );
        });

      if (response?.data) {
        const blob = documentService.getBlobConvertion(response.data);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || 'Minuta de COMPU.pdf';
        link.click();
        URL.revokeObjectURL(url);

        if (typeof onDownloaded === 'function') {
          onDownloaded();
        }
      }
    },
    async downloadSignedDocument(documentId) {
      await documentsService.downloadFromServer(documentId);
    },
    validateEditorContent() {
      if (!this.isValidEditorContent) {
        this.ShowToast(
          'Alerta',
          'No se encontró información en el Editor de Minuta. Para completar la minuta, es necesario que el Editor de Minuta contenga información.',
          'error'
        );
        return false;
      }

      return true;
    },
    validateNoPendingPlaceholders() {
      this.pendingPlaceholderLabels = this.getPendingPlaceholderLabels();

      if (!this.hasPendingPlaceholders) {
        return true;
      }

      this.ShowToast(
        'Error',
        'No se puede completar la minuta, faltan datos requeridos en el tab Editor de Minuta.',
        'error'
      );

      return false;
    },
    getPendingPlaceholderLabels() {
      const container = document.createElement('div');
      container.innerHTML = this.editorContent || '';

      const placeholderNodes = container.querySelectorAll(
        '[data-placeholder-field]'
      );

      return Array.from(placeholderNodes)
        .map((node) => this.humanizePlaceholderLabel(node.textContent))
        .filter(Boolean);
    },
    humanizePlaceholderLabel(text) {
      const match = /<\s*([^<>]+?)\s*>/.exec(text || '');

      if (!match) {
        return null;
      }

      return match[1];
    },
    async previewMinute() {
      if (!this.validateEditorContent()) {
        return;
      }

      if (!this.persistedMinuteId) {
        this.ShowToast(
          'Alerta',
          'Debe guardar la minuta antes de previsualizar.',
          'error'
        );
        return;
      }

      const response = await compuMinuteService
        .preview({
          id: this.persistedMinuteId,
          content: this.editorContent,
        })
        .catch((err) => {
          this.ShowToast(
            'Alerta',
            err?.response?.data?.message || 'Error al previsualizar la minuta.',
            'error'
          );
        });

      if (response?.data) {
        const blob = documentService.getBlobConvertion(response.data);
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    },
    async ensureMinuteExists() {
      if (!this.persistedMinuteId) {
        await this.saveGeneralInformation();
      }
    },
    buildGeneralInformationPayload({ stepId = null } = {}) {
      const student = this.generalInformation.students?.[0] ?? {};
      const hasCoordinatedAppointment =
        this.generalInformation.hasCoordinatedAppointment === true;

      return {
        id: this.persistedMinuteId,
        studentSieId: this.getStudentSieId(student),
        academicYearId: this.getObjectId(this.generalInformation.academicYear),
        locationSchoolId: this.getObjectId(this.generalInformation.school),
        townId: this.getObjectId(this.generalInformation.town),
        appointmentDate: this.generalInformation.appointmentDate,
        compuAppointmentId: hasCoordinatedAppointment
          ? this.generalInformation.compuAppointmentId ??
            this.getObjectId(this.generalInformation.coordinatedAppointment)
          : null,
        purposes: (this.generalInformation.purposes ?? []).map((purpose) => ({
          compuMinutePurpouseId: purpose.id,
          otherDescription: purpose.otherDescription,
        })),
        participants: this.getCurrentParticipants(),
        evaluations: (this.generalInformation.evaluations ?? []).map(
          (evaluation) => ({ studentEvaluationId: evaluation.studentEvaluationId })
        ),
        compuMinuteStepId: stepId,
      };
    },
    buildComparableGeneralInfoSnapshot() {
      const {
        id: _id,
        compuMinuteStepId: _stepId,
        ...comparablePayload
      } = this.buildGeneralInformationPayload();
        
      comparablePayload.participants = (comparablePayload.participants ?? []).map(
        ({ id: _participantId, ...comparableParticipant }) => comparableParticipant
      );

      return JSON.stringify(comparablePayload);
    },
    getCurrentParticipants() {
      if (
        this.activeTab === 2 &&
        this.signatureInformation?.isElectronicSignature === false &&
        this.signatureInformation?.participants
      ) {
        return this.signatureInformation.participants;
      }

      return (
        this.generalInformation?.participants ?? this.minute?.participants ?? []
      );
    },
    getObjectId(value) {
      return value?.id ?? value?.value ?? value;
    },
    getStudentSieId(student) {
      return (
        student?.studentSieId ?? student?.studentId ?? student?.sie ?? null
      );
    },
    getMinuteStepId(minute = this.minute) {
      return Number(minute?.compuMinuteStepId ?? _GeneralInformationStepId);
    },
    mergeMinuteData(data) {
      if (!data) {
        return this.minute;
      }

      const currentMinute = this.minute || {};
      const currentStudent = this.generalInformation?.students?.[0];
      const hasDataProperty = (property) =>
        Object.prototype.hasOwnProperty.call(data, property);
      const shouldClearCoordinatedAppointment =
        hasDataProperty('compuAppointmentId') &&
        data.compuAppointmentId == null;
      let coordinatedAppointment =
        currentMinute.coordinatedAppointment ??
        this.generalInformation?.coordinatedAppointment;
      let appointment = currentMinute.appointment;

      if (shouldClearCoordinatedAppointment) {
        coordinatedAppointment = null;
        appointment = null;
      } else {
        if (hasDataProperty('coordinatedAppointment')) {
          coordinatedAppointment = data.coordinatedAppointment;
        }

        if (hasDataProperty('appointment')) {
          appointment = data.appointment;
        }
      }

      return {
        ...currentMinute,
        ...data,
        student: data.student ?? currentMinute.student ?? currentStudent,
        selectedStudent: data.selectedStudent ?? currentMinute.selectedStudent,
        academicYear:
          data.academicYear ??
          currentMinute.academicYear ??
          this.generalInformation?.academicYear,
        school:
          data.school ??
          currentMinute.school ??
          this.generalInformation?.school,
        town: data.town ?? currentMinute.town ?? this.generalInformation?.town,
        municipality: data.municipality ?? currentMinute.municipality,
        coordinatedAppointment,
        compuAppointmentId: hasDataProperty('compuAppointmentId')
          ? data.compuAppointmentId
          : currentMinute.compuAppointmentId ??
            this.generalInformation?.compuAppointmentId,
        appointment,
        purposes:
          data.purposes ??
          currentMinute.purposes ??
          this.generalInformation?.purposes,
        participants:
          data.participants ??
          currentMinute.participants ??
          this.generalInformation?.participants,
        evaluations:
          data.evaluations ??
          currentMinute.evaluations ??
          this.generalInformation?.evaluations,
      };
    },
    updateMinuteData(data) {
      const mergedMinute = this.mergeMinuteData(data);

      if (!this.minute) {
        this.minute = mergedMinute;
        return;
      }

      Object.keys(mergedMinute).forEach((key) => {
        this.$set(this.minute, key, mergedMinute[key]);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.minute-tabs.custom-tab {
  margin-top: 1rem;
  --tab-font-size: 18px;
}

::v-deep .minute-tabs__content {
  display: block;
  margin-top: 1.5rem;
}

::v-deep .minute-tabs__disabled-tab {
  color: #b9b9b9 !important;
  cursor: not-allowed;
  pointer-events: none;
}

::v-deep .minute-tabs__error-tab {
  color: #dc3545 !important;
  // El subrayado del tab activo usa var(--tab-active-color) (custom-tabs);
  // la sobreescribimos acá para que, si este tab está activo Y con error,
  // el underline salga rojo en vez del verde por defecto.
  --tab-active-color: #dc3545;
}

::v-deep .minute-tabs__error-tab::before {
  content: '\26A0';
  margin-right: 0.35rem;
}
</style>
